import { setLoading } from "@/store/feature/loadingSlice";
import type { AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { toast } from "sonner";
import SessionManager from "./sessionManager";
import { refreshAccessToken } from "./tokenRefresh";
import { getServiceConfig, ServiceConfig } from "@/config/serviceConfig";

let requestCount = 0;

// Store injection to avoid circular dependency
let store: any = null;
export const injectStore = (_store: any) => {
  store = _store;
  console.log('[Axios Debug] Store injected successfully');
};

/**
 * Which service a request is for.
 *
 * Exported so a caller can name its target with types. Callers that could not reach this name
 * cast the config to `any`, which is how a request ended up routed by the interceptor's fallback
 * with nothing at the call site saying so.
 */
export type ApiServiceTarget =
  keyof ServiceConfig | 'auth' | 'safety' | 'literature' | 'ai' | 'signal' | 'gateway' | 'case-db';

/** The extra fields this instance's interceptors read from a request. */
export interface ApiRequestConfig extends AxiosRequestConfig {
  service?: ApiServiceTarget;
  meta?: { skipLoader?: boolean };
  isAuthAction?: boolean;
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  meta?: {
    skipLoader?: boolean;
  };
  service?: ApiServiceTarget;
  isAuthAction?: boolean; // If true, 401 errors will not trigger session expiry logic
  __retryCount?: number;
}

// Create axios instance with default config
// BaseURL will be set dynamically in the interceptor
const api = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  async (config: CustomAxiosRequestConfig) => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("access_token")
        : null;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Dynamic Service routing
    if (config.service) {
      const serviceConfig = await getServiceConfig();
      let serviceUrl: string | undefined;

      // Map service names to config keys
      switch (config.service) {
        case 'case-db':
          serviceUrl = serviceConfig.caseDb;
          break;
        case 'auth':
          serviceUrl = serviceConfig.authService;
          break;
        case 'safety':
          serviceUrl = serviceConfig.safetyDb;
          break;
        case 'signal':
          serviceUrl = serviceConfig.signalDb;
          break;
        case 'literature':
          serviceUrl = serviceConfig.literatureService;
          break;
        case 'ai':
          serviceUrl = serviceConfig.aiService;
          break;
        case 'gateway':
          serviceUrl = serviceConfig.regulatoryGatewayService;
          break;
        default:
          serviceUrl = serviceConfig.safetyDb;
      }

      if (serviceUrl) {
        config.baseURL = serviceUrl;
      }
    } else {
      // Fallback catch-all if no service specified? 
      // The user said: "default should taken from authe service cache ... SAFETY_DB_SERVICE which is the right things to show okay"
      // So we default to signalDb if no service is explicitly requested.
      const serviceConfig = await getServiceConfig();
      if (serviceConfig.safetyDb) {
        config.baseURL = serviceConfig.safetyDb;
      }
    }

    // Track API activity to prevent logout during active operations
    // Also syncs across tabs via storage events
    if (typeof window !== "undefined") {
      const now = Date.now().toString();
      localStorage.setItem('lastActivity', now);
      // Trigger storage event for cross-tab sync (doesn't fire in same tab)
      // Other tabs will listen via storage event in useIdleTimer
    }

    // Check if we should skip the global loader
    const skipLoader = config.meta?.skipLoader || config.headers?.['X-Skip-Loading'] === 'true';

    if (!skipLoader) {
      requestCount++;
      if (store) {
        store.dispatch(setLoading(true));
      } else {
        console.warn('[Axios Debug] Store is not injected yet, skipping setLoading(true)');
      }
    }
    return config;
  },
  (error) => {
    requestCount = Math.max(requestCount - 1, 0);
    if (requestCount === 0 && store) store.dispatch(setLoading(false));
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    requestCount = Math.max(requestCount - 1, 0);
    if (requestCount === 0 && store) store.dispatch(setLoading(false));
    return response;
  },
  async (error) => {
    requestCount = Math.max(requestCount - 1, 0);
    if (requestCount === 0 && store) store.dispatch(setLoading(false));

    // Only handle 401 errors for non-authentication endpoints
    if (error.response && error.response.status === 401) {
      // Check if this request was flagged as an explicit Auth Action (login, etc)
      const isAuthAction = error.config?.isAuthAction === true;

      if (!isAuthAction) {
        // Check if this is a retry attempt
        const retryCount = error.config?.__retryCount || 0;

        if (retryCount === 0) {
          // First 401 - attempt silent token refresh
          try {
            const refreshed = await refreshAccessToken();

            if (refreshed && refreshed.access_token) {
              // Token refresh succeeded - retry original request with new token
              // IMPORTANT: Clone config to avoid "Cannot assign to read only property" error
              const newConfig = { ...error.config };

              // Ensure headers object exists and is cloned
              newConfig.headers = { ...newConfig.headers };
              newConfig.headers["Authorization"] = `Bearer ${refreshed.access_token}`;

              newConfig.__retryCount = 1;
              return api.request(newConfig);
            } else {
              // Token refresh failed - show session expired modal
              const sessionManager = SessionManager.getInstance();
              sessionManager.handleSessionExpired();
            }
          } catch (refreshError) {
            // Token refresh error - show session expired modal
            console.error("Token refresh failed:", refreshError);
            const sessionManager = SessionManager.getInstance();
            sessionManager.handleSessionExpired();
          }
        } else {
          // Already retried once after refresh - proceed with session expiration
          const sessionManager = SessionManager.getInstance();
          sessionManager.handleSessionExpired();
        }
      }
    }

    // Check for "Your account is inactive" specific error message
    // This can come with various status codes (e.g. 403, 400), so we check the detail message
    if (error.response?.data?.detail === "Your account is inactive. Please contact your administrator.") {
      console.warn("Account is inactive, forcing logout.");
      const sessionManager = SessionManager.getInstance();
      sessionManager.handleSessionExpired();
    }

    // Global 403 handler — show a non-blocking toast for permission-denied responses.
    // Excludes auth actions (login) and the inactive-account case handled above.
    if (
      error.response?.status === 403 &&
      !error.config?.isAuthAction &&
      error.response?.data?.detail !== "Your account is inactive. Please contact your administrator."
    ) {
      toast.error("You don't have permission to perform this action.");
    }

    return Promise.reject(error);
  }
);

/**
 * Helper function to get configured base URL and headers for a service
 * This is useful for non-axios requests (e.g., SSE/fetch) that need service routing
 * @param service - Service name (e.g., 'ai', 'auth', 'safety')
 * @returns Promise with baseURL and headers
 */
export async function getServiceConfigForFetch(
  service: 'auth' | 'safety' | 'literature' | 'ai' | 'signal'
): Promise<{ baseURL: string; headers: Record<string, string> }> {
  const serviceConfig = await getServiceConfig();
  let serviceUrl: string | undefined;

  // Map service names to config keys (same logic as axios interceptor)
  switch (service) {
    case 'auth':
      serviceUrl = serviceConfig.authService;
      break;
    case 'safety':
      serviceUrl = serviceConfig.safetyDb;
      break;
    case 'signal':
      serviceUrl = serviceConfig.signalDb;
      break;
    case 'literature':
      serviceUrl = serviceConfig.literatureService;
      break;
    case 'ai':
      serviceUrl = serviceConfig.aiService;
      break;
    default:
      serviceUrl = serviceConfig.safetyDb;
  }

  const baseURL = serviceUrl || 'http://localhost:8003';

  // Get auth token (same logic as axios interceptor)
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("access_token")
      : null;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return { baseURL, headers };
}

export default api;
